import React, { useEffect, useRef, useState } from 'react';
import { DashboardLayout } from '../../components/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Badge } from '../../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../../components/ui/dialog';
import { Checkbox } from '../../components/ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Loader2, Send, Eye, Mail, Users, AlertTriangle, ShieldAlert, Trash2, BarChart3, Code2, Copy, Edit, Save, CalendarClock, X, ExternalLink } from 'lucide-react';
import api from '../../services/api';
import { toast } from 'sonner';
import { Link } from 'react-router-dom';
import BlockEmailEditor, { blocksToHtml } from '../../components/admin/BlockEmailEditor';

const EMPTY_AUDIENCE = {
  include_parents: false,
  include_customers: false,
  include_coaches: false,
  include_staff: false,
  include_admins: false,
  include_player_emails: false,
  program_ids: [],
  enrollment_status_filter: 'active_or_paused',
  tournament_ids: [],
  tournament_age_categories: [],
  tournament_payment_filter: 'paid_or_selected',
  camp_ids: [],
  player_age_brackets: [],
  birthday_month: null,
  explicit_emails: [],
  excluded_emails: [],
};

const AGE_BRACKETS = ['u10', 'u11', 'u12', 'u13', 'u14', 'u15', 'u16', 'u17', 'u18', 'u19'];

export default function EmailCampaigns() {
  const [user, setUser] = useState(null);
  const [campaigns, setCampaigns] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [programs, setPrograms] = useState([]);
  const [tournaments, setTournaments] = useState([]);
  const [camps, setCamps] = useState([]);
  const [loading, setLoading] = useState(true);

  const [showWizard, setShowWizard] = useState(false);
  const [draft, setDraft] = useState({
    name: '',
    subject: '',
    html_body: '',
    blocks: [],
    template_id: null,
    audience: { ...EMPTY_AUDIENCE, include_parents: true },
  });
  const [editorMode, setEditorMode] = useState('blocks'); // 'blocks' | 'html'
  const [showPreview, setShowPreview] = useState(false);
  const [audiencePreview, setAudiencePreview] = useState(null);
  const [previewing, setPreviewing] = useState(false);
  const [sending, setSending] = useState(false);
  const [savingDraft, setSavingDraft] = useState(false);
  const [testSending, setTestSending] = useState(false);
  // iter250: schedule-campaign modal state. The picker shows a local
  // datetime input but we tag the meaning as Eastern Time (academy TZ)
  // so admins in non-ET timezones don't get surprised. We convert ET
  // → UTC right before POST.
  const [showSchedule, setShowSchedule] = useState(false);
  const [scheduleAtEt, setScheduleAtEt] = useState('');  // "YYYY-MM-DDTHH:mm"
  const [scheduling, setScheduling] = useState(false);
  // iter244: autosave heartbeat — when the wizard is open and the
  // current draft has a `_existing_id` (i.e. has been persisted at
  // least once), PUT the latest state every 30 seconds. Shows a
  // "Saved Xs ago" indicator in the footer.
  const [lastAutosaveAt, setLastAutosaveAt] = useState(null);
  const draftRef = useRef(draft);
  const editorModeRef = useRef(editorMode);
  useEffect(() => { draftRef.current = draft; }, [draft]);
  useEffect(() => { editorModeRef.current = editorMode; }, [editorMode]);

  const isStaffOnly = user && user.role === 'staff';

  useEffect(() => {
    const u = JSON.parse(localStorage.getItem('user') || '{}');
    setUser(u);
    Promise.all([
      api.get('/email/campaigns'),
      api.get('/email/templates'),
      api.get('/programs').catch(() => ({ data: [] })),
      api.get('/tournaments').catch(() => ({ data: [] })),
      api.get('/camps').catch(() => ({ data: [] })),
    ]).then(([c, t, p, tn, cp]) => {
      setCampaigns(c.data.campaigns || []);
      setTemplates(t.data.templates || []);
      setPrograms(Array.isArray(p.data) ? p.data : (p.data.programs || []));
      setTournaments(Array.isArray(tn.data) ? tn.data : (tn.data.tournaments || []));
      setCamps(Array.isArray(cp.data) ? cp.data : (cp.data.camps || []));
    }).catch(() => toast.error('Failed to load campaigns')).finally(() => setLoading(false));
  }, []);

  // Autosave: every 30s while the wizard is open AND we have an existing
  // draft id, quietly PUT the latest state. Drafts that haven't been
  // saved-as once are skipped — we don't want to spam POSTs.
  useEffect(() => {
    if (!showWizard) return undefined;
    const tick = async () => {
      const d = draftRef.current;
      if (!d?._existing_id || isStaffOnly) return;
      if (!d.name) return;  // empty drafts aren't worth autosaving
      try {
        const html = editorModeRef.current === 'blocks' && (d.blocks || []).length > 0
          ? blocksToHtml(d.blocks)
          : (d.html_body || '');
        await api.put(`/email/campaigns/${d._existing_id}`, {
          name: d.name,
          subject: d.subject || '',
          html_body: html,
          blocks: d.blocks || [],
          audience: d.audience,
        });
        setLastAutosaveAt(Date.now());
      } catch {
        // Silent — autosave failures shouldn't disrupt the user. They
        // can hit "Update Draft" manually if the indicator stops moving.
      }
    };
    const id = setInterval(tick, 30000);
    return () => clearInterval(id);
  }, [showWizard, isStaffOnly]);

  const refresh = async () => {
    const c = await api.get('/email/campaigns');
    setCampaigns(c.data.campaigns || []);
  };

  const previewAudience = async () => {
    setPreviewing(true);
    try {
      const { data } = await api.post('/email/campaigns/preview-audience', draft.audience);
      setAudiencePreview(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Audience preview failed');
    } finally {
      setPreviewing(false);
    }
  };

  const handleTemplatePick = (tid) => {
    const t = templates.find((x) => x.id === tid);
    if (!t) return;
    setDraft((d) => ({
      ...d,
      template_id: tid,
      subject: t.subject || d.subject,
      html_body: t.body || t.html_body || d.html_body,
    }));
  };

  const handleSend = async () => {
    if (!draft.name || !draft.subject) {
      toast.error('Name and subject are required');
      return;
    }
    // In block mode, compile blocks → html before sending
    const compiledHtml = editorMode === 'blocks' && draft.blocks.length > 0
      ? blocksToHtml(draft.blocks)
      : draft.html_body;
    if (!compiledHtml) {
      toast.error('Body cannot be empty — add blocks or paste HTML');
      return;
    }
    if (!audiencePreview || audiencePreview.total === 0) {
      toast.error('Preview the audience first — it must include at least 1 recipient');
      return;
    }
    if (!window.confirm(`Send "${draft.name}" to ${audiencePreview.total} recipient(s)?`)) return;
    setSending(true);
    try {
      let campaignId = draft._existing_id;
      if (campaignId) {
        // Persist any last-minute edits to the existing draft before sending.
        await api.put(`/email/campaigns/${campaignId}`, {
          name: draft.name,
          subject: draft.subject,
          html_body: compiledHtml,
          blocks: draft.blocks || [],
          audience: draft.audience,
        });
      } else {
        const { data: created } = await api.post('/email/campaigns', { ...draft, html_body: compiledHtml });
        campaignId = created.id;
      }
      const { data: sentRes } = await api.post(`/email/campaigns/${campaignId}/send`);
      // iter246: send now runs as a background task on the server. We get
      // back a 202-ish payload with the queued recipient count; progress
      // lives on the campaign detail page (delivered_attempts / failed_count).
      toast.success(
        `Queued ${sentRes.queued_recipients ?? sentRes.sent ?? 0} emails — sending in the background. ` +
        `Watch progress on the campaign detail page.`,
        { duration: 8000 }
      );
      closeWizard();
      await refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Send failed');
    } finally {
      setSending(false);
    }
  };

  // iter250: helpers for ET-to-UTC conversion. The browser does not
  // ship an "Always-ET" timezone primitive, so we use the established
  // `Intl.DateTimeFormat(..., { timeZone: 'America/New_York' })` trick:
  // build the local moment in ET as a string, then derive UTC by
  // subtracting the ET-vs-UTC offset for that exact instant.
  const etOffsetMinutes = (dt) => {
    // Returns the offset of America/New_York vs UTC for the given Date,
    // in minutes (-300 in EDT, -240 standard DST or +240/+300 sign-flipped).
    const tz = 'America/New_York';
    const parts = new Intl.DateTimeFormat('en-US', {
      timeZone: tz, hour12: false,
      year: 'numeric', month: '2-digit', day: '2-digit',
      hour: '2-digit', minute: '2-digit', second: '2-digit',
    }).formatToParts(dt).reduce((acc, p) => { acc[p.type] = p.value; return acc; }, {});
    const asUtc = Date.UTC(
      Number(parts.year), Number(parts.month) - 1, Number(parts.day),
      Number(parts.hour) % 24, Number(parts.minute), Number(parts.second),
    );
    // Difference in minutes between the "ET clock" and real UTC clock.
    return (asUtc - dt.getTime()) / 60000;
  };

  const etInputToUtcIso = (etLocalString) => {
    // `etLocalString` is "YYYY-MM-DDTHH:mm" (no TZ) representing ET wall clock.
    // We need a real Date for that ET moment. Trick:
    //   1. Build a tentative UTC Date from the same numeric components.
    //   2. Compute the ET-vs-UTC offset for THAT instant (handles DST).
    //   3. Shift by the offset → real UTC moment.
    if (!etLocalString) return null;
    const [datePart, timePart] = etLocalString.split('T');
    if (!datePart || !timePart) return null;
    const [y, m, d] = datePart.split('-').map(Number);
    const [hh, mm] = timePart.split(':').map(Number);
    const tentativeUtc = new Date(Date.UTC(y, m - 1, d, hh, mm, 0));
    const offsetMin = etOffsetMinutes(tentativeUtc);
    const realUtc = new Date(tentativeUtc.getTime() - offsetMin * 60000);
    return realUtc.toISOString();
  };

  const formatEtTimestamp = (utcIso) => {
    if (!utcIso) return '';
    try {
      return new Date(utcIso).toLocaleString('en-US', {
        timeZone: 'America/New_York',
        month: 'short', day: 'numeric', year: 'numeric',
        hour: 'numeric', minute: '2-digit',
      }) + ' ET';
    } catch { return utcIso; }
  };

  const openScheduleModal = () => {
    // Default to 1 hour from "now in ET" so the picker is sensible.
    const now = new Date();
    const oneHourLater = new Date(now.getTime() + 60 * 60 * 1000);
    const etParts = new Intl.DateTimeFormat('en-CA', {
      timeZone: 'America/New_York', hour12: false,
      year: 'numeric', month: '2-digit', day: '2-digit',
      hour: '2-digit', minute: '2-digit',
    }).formatToParts(oneHourLater).reduce((acc, p) => { acc[p.type] = p.value; return acc; }, {});
    setScheduleAtEt(`${etParts.year}-${etParts.month}-${etParts.day}T${etParts.hour}:${etParts.minute}`);
    setShowSchedule(true);
  };

  const handleSchedule = async () => {
    if (!draft.name || !draft.subject) {
      toast.error('Name and subject are required');
      return;
    }
    const compiledHtml = editorMode === 'blocks' && draft.blocks.length > 0
      ? blocksToHtml(draft.blocks)
      : draft.html_body;
    if (!compiledHtml) {
      toast.error('Body cannot be empty — add blocks or paste HTML');
      return;
    }
    if (!audiencePreview || audiencePreview.total === 0) {
      toast.error('Preview the audience first — it must include at least 1 recipient');
      return;
    }
    const utcIso = etInputToUtcIso(scheduleAtEt);
    if (!utcIso) {
      toast.error('Pick a valid date and time');
      return;
    }
    const target = new Date(utcIso);
    if (target.getTime() - Date.now() < 5 * 60 * 1000) {
      toast.error('Schedule must be at least 5 minutes in the future');
      return;
    }
    setScheduling(true);
    try {
      let campaignId = draft._existing_id;
      if (campaignId) {
        await api.put(`/email/campaigns/${campaignId}`, {
          name: draft.name, subject: draft.subject,
          html_body: compiledHtml, blocks: draft.blocks || [],
          audience: draft.audience,
        });
      } else {
        const { data: created } = await api.post('/email/campaigns', { ...draft, html_body: compiledHtml });
        campaignId = created.id;
      }
      await api.post(`/email/campaigns/${campaignId}/schedule`, { scheduled_for: utcIso });
      toast.success(`Scheduled "${draft.name}" for ${formatEtTimestamp(utcIso)} — ${audiencePreview.total} recipient(s)`, { duration: 8000 });
      setShowSchedule(false);
      closeWizard();
      await refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Schedule failed');
    } finally {
      setScheduling(false);
    }
  };

  const handleCancelSchedule = async (id) => {
    if (!window.confirm('Cancel this scheduled send and revert to draft?')) return;
    try {
      await api.post(`/email/campaigns/${id}/cancel-schedule`);
      toast.success('Schedule cancelled — campaign is back to draft');
      await refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to cancel schedule');
    }
  };

  // iter254 — open the rendered campaign HTML in a new tab. The endpoint
  // is auth-protected so we fetch via the api client and stream into a
  // Blob URL (window.open of a raw URL can't attach headers).
  const handleViewCampaign = async (id) => {
    try {
      const { data } = await api.get(`/email/campaigns/${id}/view`, { responseType: 'text' });
      const blob = new Blob([data], { type: 'text/html' });
      const url = URL.createObjectURL(blob);
      window.open(url, '_blank', 'noopener,noreferrer');
      setTimeout(() => URL.revokeObjectURL(url), 60_000);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to open campaign body');
    }
  };

  const handleSaveDraft = async () => {
    if (!draft.name) {
      toast.error('Give the campaign a name before saving');
      return;
    }
    const compiledHtml = editorMode === 'blocks' && draft.blocks.length > 0
      ? blocksToHtml(draft.blocks)
      : draft.html_body;
    setSavingDraft(true);
    try {
      if (draft._existing_id) {
        await api.put(`/email/campaigns/${draft._existing_id}`, {
          name: draft.name,
          subject: draft.subject || '',
          html_body: compiledHtml || '',
          blocks: draft.blocks || [],
          audience: draft.audience,
        });
        toast.success('Draft updated');
      } else {
        const { data: created } = await api.post('/email/campaigns', { ...draft, html_body: compiledHtml || '' });
        // Stamp the id back on the draft so subsequent saves PUT instead of POST.
        setDraft((d) => ({ ...d, _existing_id: created.id }));
        toast.success(`Draft saved — "${created.name}"`);
      }
      await refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Save failed');
    } finally {
      setSavingDraft(false);
    }
  };

  const handleEditDraft = (c) => {
    setDraft({
      name: c.name || '',
      subject: c.subject || '',
      html_body: c.html_body || '',
      blocks: c.blocks || [],
      template_id: c.template_id || null,
      audience: { ...EMPTY_AUDIENCE, ...(c.audience || {}) },
      _existing_id: c.id,
    });
    setEditorMode((c.blocks && c.blocks.length > 0) ? 'blocks' : 'html');
    setAudiencePreview(null);
    setShowWizard(true);
  };

  const closeWizard = () => {
    setShowWizard(false);
    setDraft({ name: '', subject: '', html_body: '', blocks: [], template_id: null, audience: { ...EMPTY_AUDIENCE, include_parents: true } });
    setAudiencePreview(null);
    setEditorMode('blocks');
  };

  const handleSendTest = async () => {
    if (!draft.subject) { toast.error('Subject is required'); return; }
    const compiledHtml = editorMode === 'blocks' && draft.blocks.length > 0 ? blocksToHtml(draft.blocks) : draft.html_body;
    if (!compiledHtml) { toast.error('Body cannot be empty'); return; }
    setTestSending(true);
    try {
      const { data } = await api.post('/email/campaigns/send-test', { subject: draft.subject, html_body: compiledHtml });
      toast.success(`Test email sent to ${data.to}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Test send failed');
    } finally {
      setTestSending(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this campaign?')) return;
    try {
      await api.delete(`/email/campaigns/${id}`);
      toast.success('Deleted');
      await refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Delete failed');
    }
  };

  const handleDuplicate = async (id) => {
    try {
      const { data } = await api.post(`/email/campaigns/${id}/duplicate`);
      toast.success(`Duplicated — opening "${data.name}" for editing`);
      // Open the wizard with the duplicated draft pre-loaded.
      setDraft({
        name: data.name,
        subject: data.subject,
        html_body: data.html_body || '',
        blocks: data.blocks || [],
        template_id: data.template_id || null,
        audience: { ...EMPTY_AUDIENCE, ...(data.audience || {}) },
        _existing_id: data.id,  // we'll persist edits back to this draft on send
      });
      setEditorMode((data.blocks && data.blocks.length > 0) ? 'blocks' : 'html');
      setAudiencePreview(null);
      setShowWizard(true);
      await refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Duplicate failed');
    }
  };

  if (loading) return <DashboardLayout title="Email Campaigns"><div className="p-8 flex items-center gap-2 text-slate-500"><Loader2 className="animate-spin h-4 w-4" /> Loading…</div></DashboardLayout>;

  const setAudience = (patch) => setDraft((d) => ({ ...d, audience: { ...d.audience, ...patch } }));

  return (
    <DashboardLayout title="Email Campaigns">
    <div className="p-6 space-y-6" data-testid="email-campaigns-page">
      <div className="space-y-3">
        <div>
          <h1 className="text-2xl font-semibold">Email Campaigns</h1>
          <p className="text-sm text-slate-500">CAN-SPAM compliant marketing emails. Operational emails (invoices, reminders) bypass this surface.</p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
        <Button onClick={() => setShowWizard(true)} data-testid="new-campaign-btn">
          <Mail className="h-4 w-4 mr-2" />New Campaign
        </Button>
        </div>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">Past Campaigns</CardTitle>
          <CardDescription>Sends are immutable — once dispatched, the audience snapshot and content are preserved.</CardDescription>
        </CardHeader>
        <CardContent>
          {campaigns.length === 0 ? (
            <div className="text-center py-10 text-slate-400 text-sm">No campaigns yet. Click "New Campaign" to send your first one.</div>
          ) : (
            <div className="space-y-2">
              {campaigns.map((c) => (
                <div key={c.id} className="flex items-center justify-between p-3 border rounded-md hover:bg-slate-50" data-testid={`campaign-row-${c.id}`}>
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      <p className="font-medium">{c.name}</p>
                      <Badge variant={c.status === 'sent' ? 'default' : c.status === 'draft' ? 'outline' : c.status === 'scheduled' ? 'secondary' : 'secondary'} className={c.status === 'scheduled' ? 'bg-indigo-100 text-indigo-700' : ''}>{c.status}</Badge>
                      {c.status === 'scheduled' && c.scheduled_for && (
                        <span className="text-xs text-indigo-700 flex items-center gap-1" data-testid={`scheduled-for-${c.id}`}>
                          <CalendarClock className="h-3 w-3" />
                          {formatEtTimestamp(c.scheduled_for)}
                        </span>
                      )}
                    </div>
                    <p className="text-xs text-slate-500 mt-1">{c.subject}</p>
                    <p className="text-xs text-slate-400 mt-1">
                      {c.sent_at ? `Sent ${new Date(c.sent_at).toLocaleString()} · ` : ''}
                      {c.recipient_count > 0 ? `${c.recipient_count} recipients · ` : ''}
                      {c.opened_count > 0 ? `${c.opened_count} opens · ` : ''}
                      {c.clicked_count > 0 ? `${c.clicked_count} clicks · ` : ''}
                      {c.bounced_count > 0 ? `${c.bounced_count} bounces · ` : ''}
                      {c.complained_count > 0 ? `${c.complained_count} complaints · ` : ''}
                      by {c.sent_by_name || c.scheduled_by_name || c.created_by_name || '—'}
                    </p>
                  </div>
                  {c.status === 'sent' && (
                    <>
                      <Link to={`/admin/email-campaigns/${c.id}`} className="ml-2">
                        <Button variant="outline" size="sm" data-testid={`view-campaign-${c.id}`}><BarChart3 className="h-3 w-3 mr-1" />Analytics</Button>
                      </Link>
                    </>
                  )}
                  <Button
                    variant="outline"
                    size="sm"
                    className="ml-2 text-slate-700 border-slate-200 hover:bg-slate-50"
                    onClick={() => handleViewCampaign(c.id)}
                    title="Open the rendered campaign email in a new tab"
                    data-testid={`view-body-campaign-${c.id}`}
                  >
                    <ExternalLink className="h-3 w-3 mr-1" />View
                  </Button>
                  {c.status === 'scheduled' && (
                    <Button variant="outline" size="sm" className="ml-2 text-rose-600 border-rose-300 hover:bg-rose-50" onClick={() => handleCancelSchedule(c.id)} data-testid={`cancel-schedule-${c.id}`}>
                      <X className="h-3 w-3 mr-1" />Cancel Schedule
                    </Button>
                  )}
                  {c.status !== 'sent' && (
                    <Button variant="outline" size="sm" className="ml-2" onClick={() => handleEditDraft(c)} data-testid={`edit-campaign-${c.id}`}>
                      <Edit className="h-3 w-3 mr-1" />Edit
                    </Button>
                  )}
                  <Button variant="outline" size="sm" className="ml-2" onClick={() => handleDuplicate(c.id)} data-testid={`duplicate-campaign-${c.id}`}>
                    <Copy className="h-3 w-3 mr-1" />Duplicate
                  </Button>
                  {c.status !== 'sent' && c.status !== 'scheduled' && (
                    <Button variant="ghost" size="icon" onClick={() => handleDelete(c.id)} data-testid={`delete-campaign-${c.id}`}>
                      <Trash2 className="h-4 w-4 text-rose-600" />
                    </Button>
                  )}
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      <Dialog open={showWizard} onOpenChange={(o) => { if (!o) closeWizard(); }}>
        <DialogContent className="max-w-5xl max-h-[92vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{draft._existing_id ? 'Edit Draft Campaign' : 'New Email Campaign'}</DialogTitle>
            <DialogDescription>
              {isStaffOnly ? 'Staff sends use approved templates only — the body is locked to the chosen template.' : 'Build a marketing email. The CAN-SPAM unsubscribe footer is added automatically.'}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-5">
            {/* Template picker */}
            <div>
              <Label>Template {isStaffOnly && <span className="text-rose-600">*required</span>}</Label>
              <Select value={draft.template_id || ''} onValueChange={handleTemplatePick}>
                <SelectTrigger data-testid="campaign-template-select"><SelectValue placeholder="Optional — pick a template to seed subject + body" /></SelectTrigger>
                <SelectContent>
                  {templates.map((t) => <SelectItem key={t.id} value={t.id}>{t.name} <span className="text-xs text-slate-400">[{t.category}]</span></SelectItem>)}
                </SelectContent>
              </Select>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Campaign name (internal)</Label>
                <Input value={draft.name} onChange={(e) => setDraft((d) => ({ ...d, name: e.target.value }))} placeholder="May Newsletter" data-testid="campaign-name-input" />
              </div>
              <div>
                <Label>Subject (shown to recipients)</Label>
                <Input value={draft.subject} onChange={(e) => setDraft((d) => ({ ...d, subject: e.target.value }))} placeholder="DCC NJ News — May 2026" data-testid="campaign-subject-input" />
              </div>
            </div>

            <div>
              <div className="flex items-center justify-between mb-2">
                <Label>Body {isStaffOnly && <span className="text-xs text-slate-400">(locked to template)</span>}</Label>
                <div className="flex gap-1">
                  <Button type="button" size="sm" variant={editorMode === 'blocks' ? 'default' : 'outline'} onClick={() => setEditorMode('blocks')} disabled={isStaffOnly} data-testid="editor-mode-blocks"><Mail className="h-3 w-3 mr-1" />Block editor</Button>
                  <Button type="button" size="sm" variant={editorMode === 'html' ? 'default' : 'outline'} onClick={() => setEditorMode('html')} disabled={isStaffOnly} data-testid="editor-mode-html"><Code2 className="h-3 w-3 mr-1" />Raw HTML</Button>
                  <Button type="button" size="sm" variant="outline" onClick={() => setShowPreview(true)} data-testid="preview-email-btn"><Eye className="h-3 w-3 mr-1" />Preview</Button>
                  <Button type="button" size="sm" variant="outline" onClick={handleSendTest} disabled={testSending} data-testid="send-test-btn">
                    {testSending ? <Loader2 className="h-3 w-3 animate-spin mr-1" /> : <Send className="h-3 w-3 mr-1" />}Send Test to Me
                  </Button>
                </div>
              </div>
              {isStaffOnly || editorMode === 'html' ? (
                <Textarea
                  value={draft.html_body}
                  onChange={(e) => !isStaffOnly && setDraft((d) => ({ ...d, html_body: e.target.value }))}
                  readOnly={isStaffOnly}
                  rows={10}
                  placeholder="<p>Hello {{name}},</p><p>Your message here.</p>"
                  data-testid="campaign-body-input"
                  className="font-mono text-xs"
                />
              ) : (
                <BlockEmailEditor
                  blocks={draft.blocks}
                  onChange={(blocks) => setDraft((d) => ({ ...d, blocks }))}
                />
              )}
            </div>

            {/* Audience */}
            <Card className="bg-slate-50">
              <CardHeader className="pb-2"><CardTitle className="text-sm flex items-center gap-2"><Users className="h-4 w-4" />Audience</CardTitle></CardHeader>
              <CardContent className="space-y-3">
                <div className="grid grid-cols-2 gap-2 text-sm">
                  {[
                    ['include_parents', 'All active parents'],
                    ['include_customers', 'All customers (walk-in/lane-only)'],
                    ['include_coaches', 'Coaches'],
                    ['include_staff', 'Staff'],
                    ['include_admins', 'Admins'],
                    ['include_player_emails', 'Include players who have their own email'],
                  ].map(([k, lbl]) => (
                    <label key={k} className="flex items-center gap-2 cursor-pointer">
                      <Checkbox checked={!!draft.audience[k]} onCheckedChange={(v) => setAudience({ [k]: !!v })} data-testid={`audience-${k}`} />
                      {lbl}
                    </label>
                  ))}
                </div>

                <div>
                  <Label className="text-xs">Programs (parents enrolled)</Label>
                  <div className="flex flex-wrap gap-1.5 mt-1">
                    {programs.map((p) => (
                      <Badge
                        key={p.id}
                        variant={draft.audience.program_ids.includes(p.id) ? 'default' : 'outline'}
                        className="cursor-pointer"
                        onClick={() => setAudience({
                          program_ids: draft.audience.program_ids.includes(p.id)
                            ? draft.audience.program_ids.filter((x) => x !== p.id)
                            : [...draft.audience.program_ids, p.id],
                        })}
                      >{p.name}</Badge>
                    ))}
                  </div>
                </div>

                <div>
                  <Label className="text-xs">Tournaments (paid/selected participants)</Label>
                  <div className="flex flex-wrap gap-1.5 mt-1 max-h-32 overflow-y-auto">
                    {tournaments.map((t) => (
                      <Badge
                        key={t.id}
                        variant={draft.audience.tournament_ids.includes(t.id) ? 'default' : 'outline'}
                        className="cursor-pointer"
                        onClick={() => setAudience({
                          tournament_ids: draft.audience.tournament_ids.includes(t.id)
                            ? draft.audience.tournament_ids.filter((x) => x !== t.id)
                            : [...draft.audience.tournament_ids, t.id],
                        })}
                      >{t.name}</Badge>
                    ))}
                  </div>
                  {draft.audience.tournament_ids.length > 0 && (
                    <div className="mt-2">
                      <Label className="text-xs">Narrow to age category(ies)</Label>
                      <div className="flex flex-wrap gap-1.5 mt-1">
                        {AGE_BRACKETS.map((b) => (
                          <Badge
                            key={b}
                            variant={draft.audience.tournament_age_categories.includes(b) ? 'default' : 'outline'}
                            className="cursor-pointer"
                            onClick={() => setAudience({
                              tournament_age_categories: draft.audience.tournament_age_categories.includes(b)
                                ? draft.audience.tournament_age_categories.filter((x) => x !== b)
                                : [...draft.audience.tournament_age_categories, b],
                            })}
                          >{b.toUpperCase()}</Badge>
                        ))}
                      </div>
                    </div>
                  )}
                </div>

                {camps.length > 0 && (
                  <div>
                    <Label className="text-xs">Camps</Label>
                    <div className="flex flex-wrap gap-1.5 mt-1">
                      {camps.map((c) => (
                        <Badge
                          key={c.id}
                          variant={draft.audience.camp_ids.includes(c.id) ? 'default' : 'outline'}
                          className="cursor-pointer"
                          onClick={() => setAudience({
                            camp_ids: draft.audience.camp_ids.includes(c.id)
                              ? draft.audience.camp_ids.filter((x) => x !== c.id)
                              : [...draft.audience.camp_ids, c.id],
                          })}
                        >{c.name || c.title}</Badge>
                      ))}
                    </div>
                  </div>
                )}

                <div>
                  <Label className="text-xs">Players in age bracket (pulls parent email)</Label>
                  <div className="flex flex-wrap gap-1.5 mt-1">
                    {AGE_BRACKETS.map((b) => (
                      <Badge
                        key={b}
                        variant={draft.audience.player_age_brackets.includes(b) ? 'default' : 'outline'}
                        className="cursor-pointer"
                        onClick={() => setAudience({
                          player_age_brackets: draft.audience.player_age_brackets.includes(b)
                            ? draft.audience.player_age_brackets.filter((x) => x !== b)
                            : [...draft.audience.player_age_brackets, b],
                        })}
                      >{b.toUpperCase()}</Badge>
                    ))}
                  </div>
                </div>

                <div>
                  <Label className="text-xs">Extra individual emails (one per line)</Label>
                  <Textarea
                    rows={2}
                    value={(draft.audience.explicit_emails || []).join('\n')}
                    onChange={(e) => setAudience({ explicit_emails: e.target.value.split('\n').map((s) => s.trim()).filter(Boolean) })}
                    placeholder="newsletter-subscriber@example.com"
                    data-testid="audience-explicit-emails"
                    className="text-xs font-mono"
                  />
                </div>

                <div>
                  <Label className="text-xs flex items-center justify-between">
                    <span>Exclude these emails ({(draft.audience.excluded_emails || []).length})</span>
                    {(draft.audience.excluded_emails || []).length > 0 && (
                      <button type="button" className="text-rose-600 underline" onClick={() => setAudience({ excluded_emails: [] })}>Clear all</button>
                    )}
                  </Label>
                  <Textarea
                    rows={2}
                    value={(draft.audience.excluded_emails || []).join('\n')}
                    onChange={(e) => setAudience({ excluded_emails: e.target.value.split('\n').map((s) => s.trim().toLowerCase()).filter(Boolean) })}
                    placeholder="bob@example.com — one per line; or click the X next to a name in the preview below"
                    data-testid="audience-excluded-emails"
                    className="text-xs font-mono"
                  />
                </div>

                <div className="flex items-center justify-between bg-white border rounded p-2">
                  <Button variant="outline" size="sm" onClick={previewAudience} disabled={previewing} data-testid="preview-audience-btn">
                    {previewing ? <Loader2 className="h-3 w-3 animate-spin mr-2" /> : <Eye className="h-3 w-3 mr-2" />}
                    Preview audience
                  </Button>
                  {audiencePreview && (
                    <div className="text-xs text-right">
                      <p className="font-medium text-slate-700">{audiencePreview.total} recipient(s)</p>
                      {audiencePreview.suppressed_total > 0 && (
                        <p className="text-amber-700 flex items-center gap-1 justify-end"><ShieldAlert className="h-3 w-3" />{audiencePreview.suppressed_total} on suppression list (excluded)</p>
                      )}
                    </div>
                  )}
                </div>
                {audiencePreview && audiencePreview.sample && audiencePreview.sample.length > 0 && (
                  <div className="text-xs bg-white border rounded p-2 max-h-48 overflow-y-auto">
                    <p className="text-slate-500 mb-1">Sample (first 25 of {audiencePreview.total}) — click ✕ to exclude a recipient:</p>
                    <ul className="space-y-0.5">
                      {audiencePreview.sample.map((s, i) => (
                        <li key={i} className="flex items-center justify-between text-slate-600 hover:bg-slate-50 px-1 rounded">
                          <span className="truncate">{s.name ? `${s.name} <${s.email}>` : s.email} <span className="text-slate-400">[{s.source}]</span></span>
                          <button
                            type="button"
                            className="text-rose-600 hover:bg-rose-50 rounded px-1 ml-2 shrink-0"
                            title="Remove from this send"
                            onClick={() => {
                              const lower = (s.email || '').toLowerCase();
                              if (!lower) return;
                              const current = draft.audience.excluded_emails || [];
                              if (!current.includes(lower)) {
                                setAudience({ excluded_emails: [...current, lower] });
                                // Re-run preview so counts update.
                                setTimeout(previewAudience, 0);
                              }
                            }}
                            data-testid={`exclude-${s.email}`}
                          >✕</button>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </CardContent>
            </Card>

            <div className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded p-2 flex gap-2 items-start">
              <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" />
              <div>
                Every send here is classified as <strong>marketing</strong>. CAN-SPAM footer + one-click unsubscribe link is appended automatically.
                Recipients on the suppression list are skipped silently.
              </div>
            </div>
          </div>

          <DialogFooter>
            {lastAutosaveAt && draft._existing_id && (
              <span className="text-xs text-slate-400 mr-auto self-center" data-testid="autosave-indicator">
                Autosaved {Math.round((Date.now() - lastAutosaveAt) / 1000)}s ago
              </span>
            )}
            <Button variant="outline" onClick={closeWizard} data-testid="cancel-campaign-btn">Cancel</Button>
            <Button variant="outline" onClick={handleSaveDraft} disabled={savingDraft || isStaffOnly} data-testid="save-draft-btn">
              {savingDraft ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Save className="h-4 w-4 mr-2" />}
              {draft._existing_id ? 'Update Draft' : 'Save Draft'}
            </Button>
            <Button variant="outline" onClick={openScheduleModal} disabled={sending || scheduling || !audiencePreview || audiencePreview.total === 0} data-testid="schedule-campaign-btn" className="border-indigo-300 text-indigo-700 hover:bg-indigo-50">
              <CalendarClock className="h-4 w-4 mr-2" />
              Schedule
            </Button>
            <Button onClick={handleSend} disabled={sending || !audiencePreview || audiencePreview.total === 0} data-testid="send-campaign-btn">
              {sending ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Send className="h-4 w-4 mr-2" />}
              Send to {audiencePreview?.total || 0}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* iter250: Schedule campaign modal — Eastern Time picker. */}
      <Dialog open={showSchedule} onOpenChange={setShowSchedule}>
        <DialogContent className="max-w-md" data-testid="schedule-campaign-modal">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><CalendarClock className="h-4 w-4 text-indigo-700" />Schedule Campaign</DialogTitle>
            <DialogDescription>
              Pick the date and time (Eastern Time) when this campaign should send. Must be at least 5 minutes from now.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <div>
              <Label htmlFor="schedule-datetime-input">Send at (Eastern Time)</Label>
              <Input
                id="schedule-datetime-input"
                type="datetime-local"
                value={scheduleAtEt}
                onChange={(e) => setScheduleAtEt(e.target.value)}
                data-testid="schedule-datetime-input"
              />
              <p className="text-xs text-slate-500 mt-1">
                {scheduleAtEt && (() => {
                  const iso = etInputToUtcIso(scheduleAtEt);
                  if (!iso) return 'Invalid date';
                  const target = new Date(iso);
                  const diffMin = Math.round((target.getTime() - Date.now()) / 60000);
                  if (diffMin < 5) return <span className="text-rose-600">Must be at least 5 minutes in the future ({diffMin} min)</span>;
                  if (diffMin < 60) return <span>In {diffMin} minutes · {formatEtTimestamp(iso)}</span>;
                  if (diffMin < 60 * 24) return <span>In {Math.round(diffMin / 60)} hours · {formatEtTimestamp(iso)}</span>;
                  return <span>In {Math.round(diffMin / 60 / 24)} days · {formatEtTimestamp(iso)}</span>;
                })()}
              </p>
            </div>
            <div className="text-xs text-slate-600 bg-indigo-50 border border-indigo-200 rounded p-2">
              <strong>Audience snapshot at send time</strong> — your audience filters re-run when the campaign actually fires, so adding or removing recipients between now and then is reflected automatically.
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowSchedule(false)} data-testid="cancel-schedule-modal">Cancel</Button>
            <Button onClick={handleSchedule} disabled={scheduling || !scheduleAtEt} data-testid="confirm-schedule-btn">
              {scheduling ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <CalendarClock className="h-4 w-4 mr-2" />}
              Schedule send to {audiencePreview?.total || 0}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Email preview iframe */}
      <Dialog open={showPreview} onOpenChange={setShowPreview}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-hidden">
          <DialogHeader>
            <DialogTitle>Email Preview</DialogTitle>
            <DialogDescription>This is approximately how the email will render. Note: the CAN-SPAM unsubscribe footer is appended automatically at send time.</DialogDescription>
          </DialogHeader>
          <iframe
            title="email-preview"
            srcDoc={editorMode === 'blocks' && draft.blocks.length > 0 ? blocksToHtml(draft.blocks) : (draft.html_body || '<p style="color:#94a3b8;padding:20px;font-family:sans-serif;">Empty</p>')}
            style={{ width: '100%', height: '60vh', border: '1px solid #e5e7eb', borderRadius: 4 }}
            data-testid="email-preview-iframe"
          />
        </DialogContent>
      </Dialog>
    </div>
    </DashboardLayout>
  );
}
